import { useState, useEffect, useCallback } from "react";
import {
  handleFreezeCard as handleFreezeCardHelper,
  handleShowCardDetails as handleShowCardDetailsHelper,
  handleUpdatePin as handleUpdatePinHelper,
  handleUpdateLimit as handleUpdateLimitHelper,
  handleShowCardLimit as handleShowCardLimitHelper,
} from "../core/secureCardMethods";
import { getCardPublicKey } from "../api/api";
import type { UserCard } from "../api/types";

const DEFAULT_EXPIRY = "**/****";

// Declare the rain property on the Window interface
declare global {
  interface Window {
    rain?: {
      generateSessionId: (
        pemKey: string
      ) => Promise<{ secretKey: string; sessionId: string }>;
      decryptSecret: (
        data: string,
        iv: string,
        secretKey: string
      ) => Promise<string>;
    };
  }
}

export interface CardDetailsCallbacks {
  onToast: (message: {
    title: string;
    description: string;
    icon: string;
  }) => void;
  signTypedData: (params: any) => Promise<string>;
}

export function useCardDetails({
  userCard,
  callbacks,
  address,
}: {
  userCard: UserCard | null;
  callbacks: CardDetailsCallbacks;
  address: string;
}) {
  const [showCardDetails, setShowCardDetails] = useState(false);
  const [decryptedCardNumber, setDecryptedCardNumber] = useState<string | null>(
    null
  );
  const [decryptedCVC, setDecryptedCVC] = useState<string | null>(null);
  const [expiry, setExpiry] = useState<string>(DEFAULT_EXPIRY);
  const [isRainScriptLoaded, setIsRainScriptLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [pin, setPin] = useState<string>("");
  const [limit, setLimit] = useState<string>(""); // Current/display limit
  const [inputLimit, setInputLimit] = useState<string>(""); // Input field limit

  const { onToast, signTypedData } = callbacks;

  // Load rain.js script and check if it's already loaded
  useEffect(() => {
    if (typeof window !== "undefined" && window.rain) {
      setIsRainScriptLoaded(true);
      return;
    }
    // Manually load the script as a fallback
    const script = document.createElement("script");
    script.src = "/rain.js";
    script.async = true;
    script.onload = () => setIsRainScriptLoaded(true);
    script.onerror = (e) =>
      console.error("Error loading rain.js script manually:", e);
    document.body.appendChild(script);
    // Polling fallback
    const checkInterval = setInterval(() => {
      if (typeof window !== "undefined" && window.rain) {
        setIsRainScriptLoaded(true);
        clearInterval(checkInterval);
      }
    }, 500);
    return () => clearInterval(checkInterval);
  }, []);

  const handleShowCardDetails = useCallback(async () => {
    if (showCardDetails) {
      // Step 1: Hide card details if already visible
      setShowCardDetails(false);
      setDecryptedCardNumber(null);
      setDecryptedCVC(null);
      setExpiry(DEFAULT_EXPIRY);
      onToast({
        title: "Card Hidden",
        description: "Your card details have been hidden for your security.",
        icon: "🙈",
      });
      console.log(
        "[handleShowCardDetails] Step 0: Card details hidden by user."
      );
      return;
    }

    // Step 2: Validation
    if (!userCard || !address || !signTypedData) {
      console.error(
        "[handleShowCardDetails] Step 1: Missing wallet or card information."
      );
      onToast({
        title: "Show Card Failed",
        description: "Missing card or wallet information. Please try again.",
        icon: "⚠️",
      });
      return;
    }

    setIsLoading(true);
    console.log(
      "[handleShowCardDetails] Step 2: Begin fetch & decrypt flow..."
    );

    try {
      // Step 3: Fetch PEM public key for session
      const pemKeyResponse = await getCardPublicKey();
      const pemKey = pemKeyResponse.data;
      console.log("[handleShowCardDetails] Step 3: Received public key.");

      // Step 4: Generate session ID and secret key from Rain
      const { secretKey, sessionId } = await window.rain!.generateSessionId(
        pemKey
      );
      console.log(
        "[handleShowCardDetails] Step 4: Generated session ID from Rain."
      );

      // Step 5: Securely fetch encrypted card secrets via EIP-712-signed SDK call
      const { success, secrets, message } = await handleShowCardDetailsHelper(
        sessionId
      );

      if (!success || !secrets) {
        console.error(
          "[handleShowCardDetails] Step 5: Failed to fetch secrets:",
          message
        );
        onToast({
          title: "Failed to Show Card",
          description: message || "Unable to fetch encrypted card details.",
          icon: "❌",
        });
        return;
      }

      console.log(
        "[handleShowCardDetails] Step 6: Encrypted card data received."
      );

      // Step 6: Decrypt PAN and CVC using Rain
      const decryptedPan = await window.rain!.decryptSecret(
        secrets.encryptedPan.data,
        secrets.encryptedPan.iv,
        secretKey
      );
      const decryptedCvc = await window.rain!.decryptSecret(
        secrets.encryptedCvc.data,
        secrets.encryptedCvc.iv,
        secretKey
      );
      if (secrets.ExpirationMonth && secrets.ExpirationYear) {
        // Month should be 2 digits, e.g., "01" for January
        const month = String(secrets.ExpirationMonth).padStart(2, "0");
        // Year should be 4 digits, e.g., "2027"
        const year = String(secrets.ExpirationYear).padStart(4, "0");
        setExpiry(`${month}/${year}`);
      } else {
        setExpiry(DEFAULT_EXPIRY);
      }

      console.log(
        "[handleShowCardDetails] Step 7: Successfully decrypted card details."
      );

      // Step 7: Set decrypted values and update state
      setDecryptedCardNumber(decryptedPan);
      setDecryptedCVC(decryptedCvc);
      setShowCardDetails(true);

      onToast({
        title: "Card Revealed",
        description: "Your card details are now visible.",
        icon: "👀",
      });
    } catch (error) {
      // Step 8: Handle decryption or fetch failure
      console.error("[handleShowCardDetails] Step 8: Error occurred:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "An error occurred while showing card details.";

      onToast({
        title: "Failed to Show Card",
        description: errorMessage,
        icon: "❌",
      });
    } finally {
      // Step 9: Cleanup loading state
      setIsLoading(false);
    }
  }, [showCardDetails, userCard, address, signTypedData, onToast]);

  const handleFreezeCard = useCallback(
    async (isFreezing: boolean) => {
      // Step 1: Validation
      if (!userCard || !address || !signTypedData) {
        console.error(
          "[handleFreezeCard] Step 1: Missing wallet or card information."
        );
        onToast({
          title: "Action Failed",
          description: "Missing card or wallet information. Please try again.",
          icon: "⚠️",
        });
        return;
      }

      const action = isFreezing ? "freeze" : "unfreeze";
      const pastTense = isFreezing ? "frozen" : "unfrozen";

      console.log(
        `[handleFreezeCard] Step 2: Attempting to ${action} card ${userCard.cardID}`
      );

      // Step 2: Begin process
      setIsLoading(true);

      // Step 3: Call signed SDK freeze handler
      const { success, message } = await handleFreezeCardHelper({
        isFreezing,
      });

      if (isFreezing) {
        setShowCardDetails(false);
        setDecryptedCardNumber(null);
        setDecryptedCVC(null);
      }

      setIsLoading(false);

      // Step 4: Handle result
      if (success) {
        console.log(
          `[handleFreezeCard] Step 4: Card successfully ${pastTense}.`
        );
        onToast({
          title: `Card ${
            pastTense.charAt(0).toUpperCase() + pastTense.slice(1)
          }`,
          description: `Your card was successfully ${pastTense}.`,
          icon: "✅",
        });
      } else {
        console.error(
          `[handleFreezeCard] Step 4: Failed to ${action} card:`,
          message
        );
        onToast({
          title: `${action.charAt(0).toUpperCase() + action.slice(1)} Failed`,
          description:
            message || `Unable to ${action} your card. Please try again.`,
          icon: "❌",
        });
      }
    },
    [signTypedData, userCard, address, onToast]
  );

  const handleUpdatePin = useCallback(
    async (updatedPin: string) => {
      // Step 1: Validation
      if (updatedPin.length < 4) {
        onToast({
          title: "PIN Too Short",
          description: "PIN must be at least 4 digits long.",
          icon: "⚠️",
        });
        return;
      }

      if (!userCard || !address || !signTypedData || !isRainScriptLoaded) {
        console.error(
          "[handleUpdatePin] Step 1: Missing wallet, card, or Rain context."
        );
        onToast({
          title: "Update Failed",
          description: "Missing required card, wallet, or session info.",
          icon: "⚠️",
        });
        return;
      }

      try {
        setIsLoading(true);
        console.log("[handleUpdatePin] Step 2: Starting PIN update...");

        // Step 2: Fetch PEM key for session
        const pemKeyResponse = await getCardPublicKey();
        const pemKey = pemKeyResponse.data;
        console.log("[handleUpdatePin] Step 3: PEM key received.");

        // Step 3: Generate session from Rain
        const { secretKey, sessionId } = await window.rain!.generateSessionId(
          pemKey
        );
        console.log("[handleUpdatePin] Step 4: Session and key generated.");

        // Step 4: Call SDK handler for secure pin update
        const { success, message } = await handleUpdatePinHelper({
          pin: updatedPin,
          secretKey,
          sessionId,
        });

        // Step 5: Result handling
        if (success) {
          console.log("[handleUpdatePin] Step 5: PIN update successful.");
          onToast({
            title: "PIN Updated",
            description: "Your card PIN was updated successfully.",
            icon: "✅",
          });
        } else {
          console.error(
            "[handleUpdatePin] Step 5: PIN update failed:",
            message
          );
          onToast({
            title: "PIN Update Failed",
            description: message || "Unable to update PIN. Please try again.",
            icon: "❌",
          });
        }
      } catch (error) {
        console.error("[handleUpdatePin] Step 6: Unexpected error:", error);
        onToast({
          title: "PIN Update Failed",
          description:
            error instanceof Error
              ? error.message
              : "Something went wrong while updating your PIN.",
          icon: "❌",
        });
      } finally {
        setIsLoading(false);
        setPin(""); // Clear PIN input after update
        console.log("[handleUpdatePin] Step 7: Cleanup and reset state.");
      }
    },
    [userCard, address, signTypedData, isRainScriptLoaded, onToast]
  );

  const handleUpdateLimit = useCallback(
    async (updatedLimit: string) => {
      // Step 1: Validation
      if (updatedLimit.length === 0) {
        onToast({
          title: "Limit Required",
          description: "Please enter a transaction limit.",
          icon: "⚠️",
        });
        return;
      }

      if (!userCard || !address || !signTypedData || !isRainScriptLoaded) {
        console.error(
          "[handleUpdateLimit] Step 1: Missing wallet, card id, or Rain context."
        );
        onToast({
          title: "Update Failed",
          description: "Missing required card, wallet, or session info.",
          icon: "⚠️",
        });
        return;
      }

      try {
        setIsLoading(true);
        console.log("[handleUpdateLimit] Step 2: Starting limit update...");

        // Step 2: Call SDK handler for secure limit update
        const { success, message } = await handleUpdateLimitHelper({
          limit: updatedLimit,
        });

        // Step 3: handling result
        if (success) {
          console.log("[handleUpdateLimit] Step 3: Limit update successful.");
          setLimit(updatedLimit); // Update display limit with new value
          setInputLimit(""); // Reset input limit after successful update
          onToast({
            title: "Limit Updated",
            description:
              "Your card transaction limit was updated successfully.",
            icon: "✅",
          });
        } else {
          console.error(
            "[handleUpdateLimit] Step 4: Limit update failed:",
            message
          );
          onToast({
            title: "Limit Update Failed",
            description:
              message ||
              "Unable to update transaction limit. Please try again.",
            icon: "❌",
          });
        }
      } catch (error) {
        console.error("[handleUpdateLimit] Step 5: Unexpected error:", error);
        onToast({
          title: "Limit Update Failed",
          description:
            error instanceof Error
              ? error.message
              : "Something went wrong while updating your transaction limit.",
          icon: "❌",
        });
      } finally {
        setIsLoading(false);
        console.log("[handleUpdateLimit] Step 6: Cleanup and reset state.");
      }
    },
    [userCard, address, signTypedData, isRainScriptLoaded, onToast]
  );

  const handlePinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;

    // Block non-numeric characters
    if (!/^\d*$/.test(input)) {
      onToast({
        title: "Invalid Input",
        description: "Only digits (0–9) are allowed in your PIN.",
        icon: "⚠️",
      });
      return;
    }

    // Block PINs longer than 12 digits
    if (input.length > 12) {
      onToast({
        title: "PIN Too Long",
        description: "PIN cannot be more than 12 digits.",
        icon: "⚠️",
      });
      return;
    }

    // Valid input — update state
    setPin(input);
  };

  const handleLimitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;

    // Block non-numeric characters
    if (!/^\d*$/.test(input)) {
      onToast({
        title: "Invalid Input",
        description: "Only digits (0–9) are allowed in the limit.",
        icon: "⚠️",
      });
      return;
    }

    // Valid input — update input limit state
    setInputLimit(input);
  };

  // Fetch card limit in use effect and set as initial state
  useEffect(() => {
    const fetchCardLimit = async () => {
      if (!userCard) return;

      try {
        const { success, message, limits } = await handleShowCardLimitHelper();

        if (success && limits) {
          const currentLimit = (limits.amount / 100).toFixed(2); // Convert cents to dollars
          setLimit(currentLimit); // Set display limit
          console.log("[useCardDetails] Card limit fetched successfully.");
        } else {
          console.error(
            "[useCardDetails] Failed to fetch card limit:",
            message
          );
          onToast({
            title: "Failed to Fetch Limit",
            description: message || "Unable to retrieve card limit.",
            icon: "❌",
          });
        }
      } catch (error) {
        console.error("[useCardDetails] Error fetching card limit:", error);
        onToast({
          title: "Error Fetching Limit",
          description:
            error instanceof Error
              ? error.message
              : "An unexpected error occurred while fetching card limit.",
          icon: "❌",
        });
      }
    };

    fetchCardLimit();
  }, [userCard, onToast]);

  return {
    // Variables returned but not used in the component
    isRainScriptLoaded,
    // Things that are returned to the component
    pin,
    limit, // Display/current limit
    inputLimit, // Input field limit
    expiry,
    decryptedCardNumber,
    showCardDetails,
    decryptedCVC,
    isLoading,
    handleUpdateLimit,
    handleLimitChange,
    handleShowCardDetails,
    handleFreezeCard,
    handleUpdatePin,
    handlePinChange,
  };
}
